import { forwardRef, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import SvgIcon from '../SvgIcon';
import colors from '@/theme/colors';
import { Button, Text } from '@chakra-ui/react';

type CustomButtonProps = {
  label?: string;
  iconBeforeLabelJSX?: JSX.Element;
  iconBeforeLabelHoveredJSX?: JSX.Element;
  iconBeforeLabelSvgPath?: string;
  iconBeforeLabelSvgColor?: string;
  iconBeforeLabelSvgSize?: number | string;
  iconBeforeLabelSvgBoxWidth?: number | string;
  iconBeforeLabelSvgBoxHeight?: number | string;
  iconAfterLabelSvgPath?: string;
  iconAfterLabelSvgColor?: string;
  iconAfterLabelSvgSize?: number | string;
  buttonType?: 'button' | 'submit' | 'reset' | undefined;
  onClick?: () => void;
  disabled?: boolean;
  disabledColor?: string;
  icon?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  labelColor?: string;
  labelHoverColor?: string;
  isTransparent?: boolean;
  width?: string | number;
  height?: string | number;
  borderColor?: string;
  borderHoverColor?: string;
  borderHoverWidth?: string;
  isLoading?: boolean;
  noShadow?: boolean;
  style?: CSSProperties;
  labelStyle?: CSSProperties;
};

const colorToHoverColor: Record<string, string> = {
  'secondary.500': 'secondary.600',
  'primary.500': 'primary.600',
  'primary.400': 'primary.600',
  'extra.white': 'extra.white',
  white: 'extra.white',
  'extra.red': 'extra.darkRed',
};

const backgroundColorToDisabledColor: Record<string, string> = {
  'primary.500': 'primary.300',
  'secondary.500': 'secondary.300',
  '': 'background.gray',
};

const boxShadowColor = (backgroundColor: string | undefined) => {
  switch (backgroundColor) {
    case 'secondary.500':
    case 'secondary.600':
      return '#7D3F004D';

    case 'primary.500':
    case 'primary.600':
      return '#1A00B980';

    case 'extra.red':
      return '#7D3F004D';

    default:
      return '#7C9EBC80';
  }
};

const CustomButton = forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      label,
      iconBeforeLabelJSX,
      iconBeforeLabelHoveredJSX,
      iconBeforeLabelSvgPath,
      iconAfterLabelSvgPath,
      buttonType = 'button',
      onClick,
      disabled,
      icon,
      backgroundColor,
      disabledColor = backgroundColorToDisabledColor[backgroundColor ?? ''],
      hoverBackgroundColor = backgroundColor ? colorToHoverColor[backgroundColor] : 'transparent',
      labelColor,
      iconBeforeLabelSvgColor = labelColor ?? colors.background.blueGray,
      iconAfterLabelSvgColor = labelColor ?? colors.background.blueGray,
      iconBeforeLabelSvgSize,
      iconBeforeLabelSvgBoxWidth,
      iconBeforeLabelSvgBoxHeight,
      iconAfterLabelSvgSize,
      labelHoverColor,
      isTransparent,
      width,
      height = '50px',
      borderColor,
      borderHoverColor = 'white',
      borderHoverWidth = '2px',
      isLoading = false,
      noShadow = false,
      style,
      labelStyle,
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    const background = useMemo(() => {
      if (disabled) return disabledColor;
      if (isHovered) return hoverBackgroundColor;
      if (isTransparent) return 'transparent';
      return backgroundColor;
    }, [disabled, disabledColor, isHovered, hoverBackgroundColor, isTransparent, backgroundColor]);

    useEffect(() => {
      if (disabled) {
        setIsHovered(false);
      }
    }, [disabled]);

    return (
      <Button
        background={background}
        onMouseEnter={() => (disabled ? null : setIsHovered(true))}
        onMouseLeave={() => setIsHovered(false)}
        onClick={disabled ? () => {} : onClick}
        disabled={disabled || isLoading}
        width={width}
        height={height}
        isLoading={isLoading}
        boxShadow={isTransparent || disabled || noShadow ? '' : `0px 2px 4px 0px ${boxShadowColor(backgroundColor)}`}
        type={buttonType}
        border={
          isTransparent && isHovered
            ? `2px solid ${borderHoverColor}`
            : `2px solid ${borderColor ? borderColor : 'transparent'}`
        }
        borderWidth={isHovered ? borderHoverWidth : '2px'}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        style={style}
        ref={ref}
      >
        {iconBeforeLabelJSX && ((iconBeforeLabelHoveredJSX && !isHovered) || !iconBeforeLabelHoveredJSX)
          ? iconBeforeLabelJSX
          : null}
        {iconBeforeLabelHoveredJSX && isHovered ? iconBeforeLabelHoveredJSX : null}

        {iconBeforeLabelSvgPath ? (
          <SvgIcon
            iconPath={iconBeforeLabelSvgPath}
            size={iconBeforeLabelSvgSize}
            boxHeight={iconBeforeLabelSvgBoxHeight}
            boxWidth={iconBeforeLabelSvgBoxWidth}
            color={(isHovered ? labelHoverColor : iconBeforeLabelSvgColor) ?? iconBeforeLabelSvgColor}
          />
        ) : null}

        <Text
          color={isHovered ? (labelHoverColor ?? labelColor) : (labelColor ?? 'background.blueGray')}
          marginLeft={icon || iconBeforeLabelJSX || iconBeforeLabelSvgPath ? '10px' : 0}
          marginRight={icon || iconAfterLabelSvgPath ? '10px' : 0}
          variant={'urbanistBold'}
          style={labelStyle}
        >
          {label}
        </Text>

        {iconAfterLabelSvgPath ? (
          <SvgIcon
            iconPath={iconAfterLabelSvgPath}
            size={iconAfterLabelSvgSize}
            color={(isHovered ? labelHoverColor : iconAfterLabelSvgColor) ?? iconAfterLabelSvgColor}
          />
        ) : null}
      </Button>
    );
  },
);

export default CustomButton;
